import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env';
import { translate } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, delay, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import { AppTemplate } from '../../models/app-template.class';
import { AssetService } from '../../models/assetservice.class';
import { ApiService } from '../../services/api/api.service';
import { AlertError, AlertSuccess } from '../actions';
import * as actions from '../actions/apps.actions';
import * as sharedActions from '../actions/shared.actions';
import {
    getAppConfiguratorsMap$,
    getAppHelpDocuments$,
    getAppsLoading$,
    getAppsMetaDataMap$,
    getAppsMetaDataPermissionsMap$,
    getAppsTemplate$,
    getAssetsForSubscriptionApprovalFilters$,
    getAssetsWithLatestVersion$,
    getBridgeNodesMap$,
    getCurrentOrganizationId$,
    getPackagedAppsMap$,
    getReconConfigurations$,
    getSelectedAppEntityId$,
    getSelectedAppServiceId$,
    getSelectedPackageApp$,
    getServicesApiMap$,
    getServicesWithoutPagination$,
    getSubscribedApps$,
    getSubscribedAppsApiMap$,
    getSubscribedAppsMap$,
    getUnmappedServicesMap$,
} from '../selectors';
import { Service } from '../states';

@Injectable()
export class AppsEffects {
    private loadingAppThorughApi: { [property: string]: boolean } = {};
    private loadingPackageApps: { [property: string]: boolean } = {};
    private loadingAppThroughAppName: { [property: string]: boolean } = {};

    constructor(private actions$: Actions, private _api: ApiService, private store$: Store, private _router: Router) {}

    getSubscribedApps$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetSubscribedApps),
            delay(10),
            withLatestFrom(this.store$.select(getSubscribedApps$), this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([_action, appsMap, organizationId]) => {
                if (appsMap?.length > 0) {
                    const app = appsMap.find(service => service.restApiName === environment?.taxonomy?.appApi);
                    app?.serviceId && this.store$.dispatch(actions.GetAssetWithLatestVersion({ serviceId: app?.serviceId }));
                    this.store$.dispatch(sharedActions.CreateNavMap());
                    this.store$.dispatch(
                        actions.SetTaxonomyApp({
                            taxonomyApp: appsMap.find(service => service.restApiName === environment?.taxonomy?.appApi),
                        })
                    );
                    return [];
                }
                return from(this._api.subscriptions.getSubscribedServices()).pipe(
                    map(res => {
                        this.store$.dispatch(sharedActions.CreateNavMap());
                        this.store$.dispatch(
                            actions.SetTaxonomyApp({
                                taxonomyApp: res.find(service => service.restApiName === environment?.taxonomy?.appApi),
                            })
                        );
                        return actions.SetSubscribedApps({
                            services: res?.sort((a: AssetService, b: AssetService) => a.displayName.localeCompare(b.displayName)),
                            organizationId,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e })))
                );
            })
        )
    );

    getAssetWithLatestVersion$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetAssetWithLatestVersion),
            withLatestFrom(this.store$.select(getAssetsWithLatestVersion$), this.store$.select(getAppsLoading$)),
            mergeMap(([action, assets, appsLoadingMap]) => {
                const loading = appsLoadingMap?.[action.serviceId]?.version;
                if (assets?.[action.serviceId] || loading) {
                    return [];
                }
                this.store$.dispatch(actions.SetAppLoading({ loading: true, serviceId: action.serviceId, property: 'version' }));
                return from(this._api.assets.getAssetWithLatestVersion({ serviceId: action.serviceId })).pipe(
                    map((res: any) => {
                        this.store$.dispatch(actions.SetAppLoading({ loading: false, serviceId: action.serviceId, property: 'version' }));
                        return actions.SetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                            asset: res?.response,
                        });
                    }),
                    catchError(_e => of(actions.SetAppLoading({ loading: false, serviceId: action.serviceId, property: 'version' })))
                );
            })
        )
    );

    getAppHelpDocumentation = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAppHelpDocumentation),
            withLatestFrom(this.store$.select(getAppHelpDocuments$), this.store$.select(getServicesWithoutPagination$)),
            mergeMap(([action, helpDocuments, services]) => {
                const asset = action?.bridgeNodeId
                    ? services.find(service => service.serviceId === action.bridgeServiceId)?.bridgeNodes?.[action.bridgeNodeId]
                    : services.find(service => service.serviceId === action.serviceId);
                if (helpDocuments[action.serviceId]) {
                    return [];
                }
                return from(
                    this._api.assets.getHelpDocument({
                        serviceId: action.serviceId,
                        noLoader: true,
                    })
                ).pipe(
                    map(response =>
                        actions.SetAppHelpDocumentation({
                            helpDocuments: response as any,
                            serviceId: action.serviceId,
                            bridgeNodeId: asset.subscriptionId,
                            bridgeServiceId: action.bridgeServiceId,
                        })
                    )
                );
            })
        );
    });

    getAppBridgeNodes = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAppBridgeNodes),
            delay(500),
            withLatestFrom(
                this.store$.select(getBridgeNodesMap$),
                this.store$.select(getAppsLoading$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, bridgeNodes, appsLoadingMap, versionsMap]) => {
                const loading = appsLoadingMap?.[action.serviceId]?.bridgeNodes;
                if (!versionsMap?.[action.serviceId]?.metadataId) {
                    this.store$.dispatch(
                        actions.GetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                        })
                    );
                    setTimeout(() => {
                        this.store$.dispatch(actions.GetAppBridgeNodes(action));
                    }, 500);
                    return [];
                }
                const assetId = versionsMap?.[action.serviceId]?.metadataId;
                if (bridgeNodes[assetId] || loading) {
                    return [];
                }
                this.store$.dispatch(actions.SetAppLoading({ loading: true, serviceId: action.serviceId, property: 'bridgeNodes' }));
                return this._api.subscriptions.fetchBridgeNodes(assetId).pipe(
                    map(response => {
                        Object.keys(response || {}).forEach(key => {
                            response[key]['assetMetaUId'] = response?.[key]?.id;
                            const node = response?.[key];
                            if (node.componentType === 'ASSET') {
                                node['reports'] = [];
                                node.nextNodes.forEach(serviceNode => {
                                    const nextNode = response[serviceNode];
                                    nextNode?.componentType === 'FILE' && node['reports'].push(nextNode);
                                });
                            }
                        });
                        this.store$.dispatch(
                            actions.SetAppLoading({ loading: false, serviceId: action.serviceId, property: 'bridgeNodes' })
                        );
                        return actions.SetAppBridgeNodes({
                            assetId: assetId,
                            bridgeNodes: response,
                            serviceId: action.serviceId,
                        });
                    }),
                    catchError(error => {
                        this.store$.dispatch(
                            actions.SetAppLoading({ loading: false, serviceId: action.serviceId, property: 'bridgeNodes' })
                        );
                        return of(AlertError({ message: error }));
                    })
                );
            })
        );
    });

    getAppMetaData = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAppMetaData),
            delay(10),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getAppsMetaDataMap$),
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getAppsLoading$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, organizationId, metaDataMap, appsMap, appsLoading, versionsMap]) => {
                if (!action.serviceId) {
                    return [];
                }
                const assetId = action.assetId || appsMap?.[action.serviceId]?.assetMetaUId || versionsMap?.[action.serviceId]?.metadataId;
                const restApiName = appsMap?.[action.serviceId]?.restApiName || versionsMap?.[action.serviceId]?.restApiName;
                if (!assetId || !restApiName) {
                    setTimeout(() => {
                        this.store$.dispatch(actions.GetAppMetaData(action));
                    }, 1000);
                    this.store$.dispatch(
                        actions.GetAssetWithLatestVersion({
                            serviceId: action.serviceId,
                        })
                    );
                    return [];
                }
                const loading = appsLoading?.[action.serviceId]?.meta;
                const app = appsMap?.[action.serviceId] || (versionsMap?.[action.serviceId] as any);
                const metaData = metaDataMap?.[action.serviceId]?.[assetId];
                const bridgeLoading = appsLoading?.[action.bridgeServiceId]?.bridgeNodes;
                if (
                    loading ||
                    app?.assetType === 'BRIDGE_ASSET' ||
                    bridgeLoading ||
                    (!app && action?.['bridgeServiceId']?.length > 0 && bridgeLoading === undefined)
                ) {
                    action.bridgeServiceId &&
                        setTimeout(() => {
                            this.store$.dispatch(actions.GetAppMetaData(action));
                        }, 1500);
                    return [];
                }
                if (metaData?.uid) {
                    return [];
                }
                if (!app && Object.keys(appsMap || {}).length === 0 && !action.guest) {
                    setTimeout(() => {
                        this.store$.dispatch(actions.GetAppMetaData(action));
                    }, 100);
                    return [];
                } else if (!app && !action.guest) {
                    return of(AlertError({ message: 'Failed to get app meta data as it is not subscribed' }));
                }
                this.store$.dispatch(actions.SetAppLoading({ loading: true, serviceId: action.serviceId }));
                if (!app?.restApiName) {
                    return [];
                }
                return from(
                    this._api.assets.getAssetMetaDataViaPromise({
                        organizationId,
                        assetMetaUId: assetId,
                        name: app?.serviceName,
                        restApiName: app?.restApiName,
                        noAlerts: action.noAlerts,
                    })
                ).pipe(
                    map(response => {
                        this.store$.dispatch(actions.SetAppLoading({ loading: false, serviceId: action.serviceId }));
                        return actions.SetAppMetaData({
                            metaData: response,
                            serviceId: action.serviceId,
                        });
                    }),
                    catchError(() => of(actions.SetAppLoading({ loading: false, serviceId: action.serviceId })))
                );
            })
        );
    });

    getAssetMetaDataPermissions = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAppMetaDataPermissions),
            withLatestFrom(this.store$.select(getAppsMetaDataPermissionsMap$)),
            mergeMap(([action, metaDataPermissionsMap]) => {
                if (metaDataPermissionsMap?.[action.serviceId]) {
                    return [];
                }
                return from(
                    this._api.assets.getAssetMetaDataPermissions({
                        serviceId: action.serviceId,
                        assetType: action.assetType,
                        assetName: action.assetName,
                        noAlerts: action.noAlerts,
                    })
                ).pipe(
                    map(response => {
                        return actions.SetAppMetaDataPermissions({
                            metaDataPermissions: response,
                            serviceId: action.serviceId,
                        });
                    }),
                    catchError(response => of(AlertError({ message: response.msg || 'Failed to get app meta data permissions' })))
                );
            })
        );
    });

    getReconConfiguration = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetReconConfiguration),
            delay(10),
            withLatestFrom(
                this.store$.select(getReconConfigurations$),
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getSubscribedAppsApiMap$),
                this.store$.select(getAppsLoading$)
            ),
            mergeMap(([action, configs, appsMap, appsApiMap, appsLoading]) => {
                const loading = appsLoading?.[action.serviceId]?.reconConfiguration;
                if (loading) {
                    return [];
                }
                if (configs?.[action.serviceId]) {
                    return [];
                }
                const app = appsMap?.[action.serviceId] || appsApiMap?.[action.apiName];
                if (!app && Object.keys(appsMap || {}).length === 0) {
                    setTimeout(() => {
                        this.store$.dispatch(actions.GetReconConfiguration(action));
                    }, 100);
                    return [];
                } else if (!app?.restApiName) {
                    this.store$.dispatch(actions.GetAppDetailsThroughApi({ apiName: action?.apiName }));
                    setTimeout(() => {
                        this.store$.dispatch(actions.GetReconConfiguration(action));
                    }, 1000);
                    return [];
                } else if (!app) {
                    return of(AlertError({ message: 'Failed to get app meta data as it is not subscribed' }));
                }
                this.store$.dispatch(actions.SetAppLoading({ loading: true, serviceId: action.serviceId, property: 'reconConfig' }));
                return from(
                    this._api.assets.getReconciliationConfiguration({
                        restApiName: app.restApiName,
                        serviceId: app.serviceId,
                        dontTransform: true,
                        noAlerts: action.noAlerts,
                    })
                ).pipe(
                    map(response => {
                        this.store$.dispatch(
                            actions.SetAppLoading({ loading: false, serviceId: action.serviceId, property: 'reconConfig' })
                        );
                        return actions.SetReconConfiguration({
                            configuration: response,
                            serviceId: action.serviceId,
                        });
                    }),
                    catchError(() => of(actions.SetAppLoading({ loading: false, serviceId: action.serviceId, property: 'reconConfig' })))
                );
            })
        );
    });

    routeAppsCategory = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.RouteSelectedAppCategory),
            delay(10),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getSelectedPackageApp$),
                this.store$.select(getSelectedAppServiceId$)
            ),
            mergeMap(([action, organizationId, appsMap, packageApp, serviceId]) => {
                if (!serviceId) {
                    return of(AlertError({ message: 'Service id not assigned' }));
                }
                const app = appsMap?.[serviceId];
                if (!app && Object.keys(appsMap || {}).length === 0) {
                    return of(actions.RouteSelectedAppCategory(action));
                } else if (!app) {
                    return of(AlertError({ message: 'Failed to route to category as it is not subscribed' }));
                }
                const routes = ['organizations', organizationId];
                if (packageApp?.restApiName?.length > 0) {
                    routes.push('packages', packageApp.restApiName);
                }
                if (app['bridge']) {
                    routes.push('bridges', app['bridge'].restApiName);
                }
                routes.push('apps', app.restApiName, 'configure-app', action.category);
                this._router.navigate(routes);
                return [];
            })
        );
    });

    getAppTemplate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAppMetaData, actions.GetAppTemplate),
            delay(10),
            withLatestFrom(
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getAppsLoading$),
                this.store$.select(getAppsTemplate$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, organizationId, appsMap, appsLoading, appsTemplate, versionsMap]) => {
                const serviceId = action.serviceId;
                if (!serviceId) {
                    return [];
                }
                const app = appsMap?.[serviceId] || (versionsMap?.[serviceId] as any);
                const loading = appsLoading?.[serviceId]?.template;
                const bridgeLoading = appsLoading?.[action?.['bridgeServiceId']]?.bridgeNodes;
                if (
                    loading ||
                    app?.assetType === 'BRIDGE_ASSET' ||
                    bridgeLoading ||
                    (!app && action?.['bridgeServiceId']?.length > 0 && bridgeLoading === undefined)
                ) {
                    return [];
                }
                const template = appsTemplate?.[serviceId];
                if (template && !action.forceFetch) {
                    return [];
                }
                if (!app && Object.keys(appsMap || {}).length === 0 && !action.guest) {
                    setTimeout(() => {
                        this.store$.dispatch(actions.GetAppMetaData(action));
                    }, 100);
                    return [];
                } else if (!app && !action.guest) {
                    return of(AlertError({ message: 'Failed to get app template as it is not subscribed' }));
                }
                if (!app?.restApiName) {
                    return [];
                }
                this.store$.dispatch(actions.SetAppLoading({ loading: true, serviceId, property: 'template' }));
                return this._api.assets.getAppTemplate(app?.restApiName, action.noAlerts).pipe(
                    map(response => {
                        this.store$.dispatch(actions.SetAppLoading({ loading: false, serviceId, property: 'template' }));
                        return actions.SetAppTemplate({
                            organizationId,
                            template: response,
                            serviceId,
                        });
                    }),
                    catchError(_e => {
                        // this.store$.dispatch(AlertError({ message: e?.msg }));
                        this.store$.dispatch(actions.SetAppLoading({ loading: false, serviceId: serviceId, property: 'template' }));
                        return of(
                            actions.SetAppTemplate({
                                organizationId,
                                template: { isOfCurrentOrg: false, uiTemplate: { uid: app.assetMetaUId, entities: [] } as any },
                                serviceId,
                            })
                        );
                    })
                );
            })
        );
    });

    submitAppTemplate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.AppSubmitTemplate),
            delay(10),
            withLatestFrom(
                this.store$.select(getSelectedAppServiceId$),
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getAppsMetaDataMap$),
                this.store$.select(getAppsTemplate$),
                this.store$.select(getSelectedAppEntityId$),
                this.store$.select(getAppsLoading$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, serviceId, appsMap, metaDataMap, appsTemplate, entityId, appsLoading, version]) => {
                const template = appsTemplate?.[serviceId]?.uiTemplate;
                const metaData = metaDataMap?.[serviceId];
                const app = appsMap?.[serviceId];
                const payload = new AppTemplate(metaData?.[version?.[app?.serviceId]?.metadataId || app?.assetMetaUId], template);
                const loading = appsLoading?.[serviceId]?.template;
                action.details && payload.setAppDetails(action.details);
                action.entitiesOrder && payload.setEntitiesOrder(action.entitiesOrder, undefined);
                action.entityDetails && payload.setAppDetails(undefined, entityId, action.entityDetails);
                action.fieldDetails && payload.setAppDetails(undefined, entityId, undefined, action.fieldDetails);
                action.workflowDetails && payload.setWorkflowDetails(action.workflowDetails);
                if (loading) {
                    return [];
                }
                this.store$.dispatch(actions.SetAppLoading({ loading: true, serviceId, property: 'template' }));
                return from(this._api.assets.submitAppTemplate(app.restApiName, payload)).pipe(
                    map(() => {
                        this.store$.dispatch(actions.SetAppLoading({ loading: false, serviceId, property: 'template' }));
                        this.store$.dispatch(AlertSuccess({ message: translate('Configuration updated') }));
                        return actions.GetAppTemplate({
                            serviceId,
                            noAlerts: true,
                            forceFetch: true,
                        });
                    }),
                    catchError(e => {
                        this.store$.dispatch(AlertError({ message: e?.msg }));
                        return of(actions.SetAppLoading({ loading: false, serviceId: serviceId, property: 'template' }));
                    })
                );
            })
        );
    });

    deleteAppTemplate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.AppDeleteTemplate),
            delay(10),
            withLatestFrom(
                this.store$.select(getSelectedAppServiceId$),
                this.store$.select(getAppsTemplate$),
                this.store$.select(getAppsLoading$),
                this.store$.select(getSubscribedAppsMap$)
            ),
            mergeMap(([_action, serviceId, appsTemplate, appsLoading, appsMap]) => {
                const loading = appsLoading?.[serviceId]?.template;
                const app = appsMap?.[serviceId];
                if (loading) {
                    return [];
                }
                if (!appsTemplate?.[serviceId]?.uiTemplate?.uid) {
                    return of(AlertError({ message: translate('No template found to delete') }));
                }
                this.store$.dispatch(actions.SetAppLoading({ loading: true, serviceId, property: 'template' }));
                return from(this._api.assets.deleteAppTemplate(app?.restApiName)).pipe(
                    map(() => {
                        this.store$.dispatch(actions.SetAppLoading({ loading: false, serviceId, property: 'template' }));
                        this.store$.dispatch(AlertSuccess({ message: translate('Configuration reset successful') }));
                        return actions.GetAppTemplate({
                            serviceId,
                            noAlerts: true,
                            forceFetch: true,
                        });
                    }),
                    catchError(e => {
                        this.store$.dispatch(AlertError({ message: e?.msg }));
                        return of(actions.SetAppLoading({ loading: false, serviceId: serviceId, property: 'template' }));
                    })
                );
            })
        );
    });

    getAppConfigurator$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAppMetaData, actions.GetAppConfigurator),
            withLatestFrom(
                this.store$.select(getSubscribedAppsMap$),
                this.store$.select(getAppsLoading$),
                this.store$.select(getAppConfiguratorsMap$),
                this.store$.select(getAssetsWithLatestVersion$)
            ),
            mergeMap(([action, appsMap, appsLoading, appConfiguratorMap, currentServicesMap]) => {
                const serviceId = action.serviceId;
                if (!serviceId) {
                    return [];
                }
                if (!currentServicesMap?.[serviceId]) {
                    setTimeout(() => {
                        this.store$.dispatch(actions.GetAppMetaData(action));
                    }, 1000);
                    this.store$.dispatch(
                        actions.GetAssetWithLatestVersion({
                            serviceId,
                        })
                    );
                    return [];
                }
                const app = appsMap?.[serviceId] || (currentServicesMap?.[serviceId] as any);
                const loading = appsLoading?.[serviceId]?.configurator;
                const bridgeLoading = appsLoading?.[action.bridgeServiceId]?.bridgeNodes;
                if (loading || app?.assetType === 'BRIDGE_ASSET' || bridgeLoading) {
                    return [];
                }
                const assetId = action.assetId || app?.assetMetaUId || currentServicesMap?.[serviceId]?.metadataId;
                const configurator = appConfiguratorMap?.[serviceId]?.[assetId];
                if (configurator && !action.forceFetch) {
                    return [];
                }
                if (!app && Object.keys(appsMap || {}).length === 0 && !action.guest) {
                    setTimeout(() => {
                        this.store$.dispatch(actions.GetAppMetaData(action));
                    }, 100);
                    return [];
                } else if (!app && !action.guest) {
                    return of(AlertError({ message: 'Failed to get app as it is not subscribed' }));
                }
                this.store$.dispatch(actions.SetAppLoading({ loading: true, serviceId, property: 'configurator' }));
                if (!app?.restApiName) {
                    return [];
                }
                return from(
                    this._api.assets.getAppConfigurator({
                        serviceId,
                        assetMetaUID: assetId,
                        restApiName: app?.restApiName,
                        noAlerts: action.noAlerts,
                    })
                ).pipe(
                    map(response => {
                        this.store$.dispatch(actions.SetAppLoading({ loading: false, serviceId, property: 'configurator' }));
                        return actions.SetAppConfigurator({
                            serviceId,
                            assetMetaUID: assetId,
                            configurator: response,
                        });
                    }),
                    catchError(e => {
                        this.store$.dispatch(actions.SetAppLoading({ loading: false, serviceId, property: 'configurator' }));
                        if (e?.msg) {
                            this.store$.dispatch(AlertError({ message: e?.msg }));
                            return [];
                        }
                        return of(
                            actions.SetAppConfigurator({
                                serviceId,
                                assetMetaUID: assetId,
                                configurator: {},
                            })
                        );
                    })
                );
            })
        );
    });

    getAssetsForSubscriptionApprovalFilters = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAssetsForSubscriptionApprovalFilters),
            withLatestFrom(this.store$.select(getAssetsForSubscriptionApprovalFilters$)),
            mergeMap(([_action, assets]) => {
                if (assets?.length > 0) {
                    return [];
                }
                return from(this._api.subscriptions.getAssetsForSubscriptionApprovalFilters()).pipe(
                    map(response =>
                        actions.SetAssetsForSubscriptionApprovalFilters({
                            assets: response,
                        })
                    ),
                    catchError(e => of(AlertError({ message: e })))
                );
            })
        );
    });

    getPackageApps = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetPackageApps),
            delay(100),
            withLatestFrom(this.store$.select(getPackagedAppsMap$)),
            mergeMap(([action, packagedApps]) => {
                if (packagedApps?.[action.serviceId]?.length > 0) {
                    return [];
                }
                if (this.loadingPackageApps?.[action.serviceId]) {
                    return [];
                }
                this.loadingPackageApps[action.serviceId] = true;
                return this._api.assets.getPackageApps(action.serviceId).pipe(
                    map((response: any) => {
                        this.loadingPackageApps[action.serviceId] = false;
                        return actions.SetPackageGroupApps({
                            serviceId: action.serviceId,
                            packagedApps: response?.packageItems || [],
                        });
                    }),
                    catchError(e => {
                        this.loadingPackageApps[action.serviceId] = false;
                        return of(AlertError({ message: e }));
                    })
                );
            })
        )
    );

    getAppDetailsThroughApi$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetAppDetailsThroughApi),
            withLatestFrom(this.store$.select(getServicesApiMap$), this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([action, servicesMap, organizationId]) => {
                if (!action.apiName || servicesMap?.[action.apiName] || this.loadingAppThorughApi?.[action.apiName]) {
                    return [];
                }
                this.loadingAppThorughApi[action.apiName] = true;
                return this._api.assets.getAppDetailsThroughApi(action.apiName).pipe(
                    map(response => {
                        this.loadingAppThorughApi[action.apiName] = false;
                        return actions.SetAppDetailsThroughApi({
                            apiName: action.apiName,
                            appDetails: response,
                            organizationId,
                        });
                    }),
                    catchError(_e => {
                        this.loadingAppThorughApi[action.apiName] = false;
                        return of(
                            actions.SetAppDetailsThroughApi({
                                apiName: action.apiName,
                                appDetails: {
                                    restApiName: action.apiName,
                                },
                                organizationId,
                            })
                        );
                    })
                );
            })
        )
    );

    getAppThroughAppName$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAppThroughAppName),
            delay(10),
            withLatestFrom(this.store$.select(getUnmappedServicesMap$), this.store$.select(getCurrentOrganizationId$)),
            mergeMap(([action, nonMappedServices, organizationId]) => {
                if (nonMappedServices?.find(s => s.serviceName === action.appName)) {
                    return [];
                }
                if (this.loadingAppThroughAppName?.[action.appName]) {
                    return [];
                }
                this.loadingAppThroughAppName[action.appName] = true;
                return this._api.subscriptions.getAllServices(action.appName).pipe(
                    map((response: Service[]) => {
                        this.loadingAppThroughAppName[action.appName] = false;
                        return actions.GetAppDetailsThroughApi({
                            apiName: response?.[0]?.restApiName,
                        });
                    }),
                    catchError(_e => {
                        this.loadingAppThroughAppName[action.appName] = false;
                        return of(
                            actions.SetAppDetailsThroughApi({
                                apiName: action.appName,
                                appDetails: {
                                    restApiName: action.appName,
                                    serviceName: action.appName,
                                },
                                organizationId,
                            })
                        );
                    })
                );
            })
        );
    });
}
